import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { relTime } from "@/lib/api";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCheck, Sparkles, Inbox as InboxIcon, ArrowRightLeft, Calendar } from "lucide-react";

const ICONS = { new_lead: Sparkles, transfer: ArrowRightLeft, booking: Calendar, escalation: InboxIcon };

export default function NotificationsBell({ compact = false }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: count = { count: 0 } } = useQuery({
    queryKey: ["notif-count"],
    queryFn: () => api.get("/notifications/unread-count").then((r) => r.data),
    refetchInterval: 15000,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["notifs"],
    queryFn: () => api.get("/notifications").then((r) => r.data),
    enabled: open,
  });

  const markAll = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notif-count"] });
    qc.invalidateQueries({ queryKey: ["notifs"] });
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    qc.invalidateQueries({ queryKey: ["notif-count"] });
    qc.invalidateQueries({ queryKey: ["notifs"] });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className={`relative rounded-sm border border-border ${compact ? "p-1.5" : "p-2"} hover:bg-secondary/60 transition-colors`}>
          <Bell className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} text-muted-foreground`} />
          {count.count > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
              {count.count > 99 ? "99+" : count.count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-sm bg-card border-border max-h-[70vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Notifications</div>
            <div className="font-display font-extrabold text-lg">{count.count > 0 ? `${count.count} unread` : "You're all caught up."}</div>
          </div>
          {count.count > 0 && (
            <button onClick={markAll} data-testid="notifications-mark-all" className="text-xs uppercase tracking-widest text-primary hover:underline flex items-center gap-1"><CheckCheck className="h-3 w-3" />Mark all read</button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No notifications yet.</div>}
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            const content = (
              <div className={`p-3 border-b border-border/60 flex items-start gap-3 ${n.read ? "opacity-60" : "bg-primary/5"}`}>
                <div className={`h-8 w-8 rounded-sm flex items-center justify-center flex-none ${n.read ? "bg-secondary" : "bg-primary/20"}`}><Icon className="h-4 w-4 text-primary" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{n.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-1">{relTime(n.created_at)}</div>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-primary flex-none mt-1.5" />}
              </div>
            );
            return n.link ? (
              <Link key={n.id} to={n.link} onClick={() => { markOne(n.id); setOpen(false); }} data-testid="notification-item">{content}</Link>
            ) : (
              <button key={n.id} onClick={() => markOne(n.id)} className="block w-full text-left" data-testid="notification-item">{content}</button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
